import { IamGroupType, SmoketestPermissionLevel, UserGroupRecordListPartner } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { useDebounceValue } from 'noddi-util';
import { useRef, useState } from 'react';

import { ApiErrorMessageInternal, Popover, PopoverAnchor, PopoverContent } from 'noddi-ui-web';
import { UserInfoCell, type UserInfoCellUser } from '../UserGroups/UserInfoCell';

const MENTION_ROLES: SmoketestPermissionLevel[] = [
  SmoketestPermissionLevel.STAFF,
  SmoketestPermissionLevel.WORKER,
  SmoketestPermissionLevel.SUPERUSER
];
const MENTION_GROUP_TYPES: IamGroupType[] = [IamGroupType.PERSONAL];

export type Mention = { id: number; name: string };

type Props = {
  value: string;
  onChange: (value: string) => void;
  mentions: Mention[];
  onMentionsChange: (mentions: Mention[]) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
};

export const MentionTextarea = ({
  value,
  onChange,
  mentions,
  onMentionsChange,
  placeholder,
  rows = 2,
  className
}: Props) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [trigger, setTrigger] = useState<{ start: number; query: string } | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const debouncedQuery = useDebounceValue({ value: trigger?.query ?? '', delay: 150 });

  const mentionedIds = mentions.map((m) => m.id);

  const { data, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroups,
    input: {
      pageIndex: 0,
      pageSize: 8,
      search: debouncedQuery,
      roles: MENTION_ROLES,
      types: MENTION_GROUP_TYPES
    },
    queryConfig: { enabled: trigger !== null }
  });

  const candidates: Candidate[] =
    data?.results.map(toCandidate).filter((c): c is Candidate => c !== null && !mentionedIds.includes(c.userId)) ?? [];

  const safeHighlight = candidates.length === 0 ? 0 : Math.min(highlightIndex, candidates.length - 1);

  const updateText = (next: string) => {
    onChange(next);
    const stillPresent = mentions.filter((m) => next.includes(`@${m.name}`));
    if (stillPresent.length !== mentions.length) {
      onMentionsChange(stillPresent);
    }
  };

  const detectTrigger = (text: string, caret: number) => {
    // eslint-disable-next-line no-restricted-syntax -- backward index scan from the caret to find the @ trigger
    for (let i = caret - 1; i >= 0; i--) {
      const ch = text.charAt(i);
      if (ch === '@') {
        const prev = i === 0 ? '' : text.charAt(i - 1);
        if (prev === '' || /\s/.test(prev)) {
          const query = text.substring(i + 1, caret);
          if (!/\s/.test(query)) {
            if (query !== trigger?.query) {
              setHighlightIndex(0);
            }
            setTrigger({ start: i, query });
            return;
          }
        }
        break;
      }
      if (/\s/.test(ch)) {
        break;
      }
    }
    setTrigger(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.target.value;
    updateText(next);
    detectTrigger(next, event.target.selectionStart);
  };

  const handleSelectionChange = () => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    detectTrigger(el.value, el.selectionStart);
  };

  const insertMention = (candidate: Candidate) => {
    if (!trigger || !textareaRef.current) {
      return;
    }
    const el = textareaRef.current;
    const caret = el.selectionStart;
    const before = value.substring(0, trigger.start);
    const after = value.substring(caret);
    const insertion = `@${candidate.name} `;
    const next = before + insertion + after;
    onChange(next);
    onMentionsChange([...mentions, { id: candidate.userId, name: candidate.name }]);
    setTrigger(null);
    requestAnimationFrame(() => {
      const cursor = (before + insertion).length;
      el.focus();
      el.setSelectionRange(cursor, cursor);
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!trigger || candidates.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((i) => (i + 1) % candidates.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((i) => (i - 1 + candidates.length) % candidates.length);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      const selected = candidates[safeHighlight];
      if (selected) {
        insertMention(selected);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setTrigger(null);
    }
  };

  return (
    <Popover open={trigger !== null && (candidates.length > 0 || !!error)}>
      <PopoverAnchor asChild>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onSelect={handleSelectionChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={rows}
          className={cn(
            'w-full resize-none border-0 bg-transparent text-sm outline-hidden placeholder:text-muted-foreground',
            className
          )}
        />
      </PopoverAnchor>
      <PopoverContent
        align='start'
        side='bottom'
        sideOffset={4}
        className='w-72 p-1'
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {error ? <ApiErrorMessageInternal error={error} /> : null}
        <ul className='flex flex-col'>
          {candidates.map((candidate, index) => (
            <li key={candidate.userId}>
              <button
                type='button'
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertMention(candidate);
                }}
                onMouseEnter={() => setHighlightIndex(index)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                  index === safeHighlight && 'bg-accent text-accent-foreground'
                )}
              >
                <UserInfoCell user={candidate.user} />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

type Candidate = { userId: number; name: string; user: UserInfoCellUser };

function toCandidate(group: UserGroupRecordListPartner): Candidate | null {
  const user = group.users?.[0];
  if (!user) {
    return null;
  }
  return {
    userId: user.id,
    name: group.name,
    user: {
      firstName: user.firstName,
      lastName: user.lastName,
      name: group.name,
      phoneNumber: user.phoneNumber,
      email: user.email ?? undefined
    }
  };
}
